import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Calendar, Clock, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { BookingDetails } from '@/hooks/booking/useBookingDetails';
import { parseStoredDateTime } from '@/utils/timeUtils';

interface BookingHeaderProps {
  booking: BookingDetails;
}

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'new':
      return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
    case 'accepted':
      return 'bg-green-500/10 text-green-700 dark:text-green-400';
    case 'completed':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    case 'cancelled':
    case 'declined':
      return 'bg-red-500/10 text-red-700 dark:text-red-400';
    case 'blocked':
      return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
    default:
      return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
  }
};

export const BookingHeader = ({ booking }: BookingHeaderProps) => {
  // Handle both datetime strings and separate date/time values
  const startDateTime = booking.start_time ? new Date(booking.start_time) : null;
  const endDateTime = booking.end_time ? new Date(booking.end_time) : null;

  return (
    <div className="bg-card rounded-lg border p-6 space-y-4">
      {/* Status and ID */}
      <div className="flex items-center justify-between">
        <Badge className={getStatusColor(booking.status)}>
          {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1)}
        </Badge>
        <span className="text-sm text-muted-foreground">#{booking.id}</span>
      </div>

      {/* Service Name */}
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-2">
          {booking.service?.name}
        </h1>
      </div>

      {/* Date and Time */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex items-center gap-2 text-foreground">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {startDateTime && !isNaN(startDateTime.getTime()) 
              ? format(startDateTime, 'EEEE, MMMM d, yyyy') 
              : 'Invalid date'
            }
          </span>
        </div>
        
        <div className="flex items-center gap-2 text-foreground">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {startDateTime && endDateTime && 
             !isNaN(startDateTime.getTime()) && !isNaN(endDateTime.getTime())
              ? `${format(startDateTime, 'h:mm a')} - ${format(endDateTime, 'h:mm a')}`
              : 'Invalid time'
            }
          </span>
        </div>

        {booking.service?.price && (
          <div className="flex items-center gap-2 text-foreground">
            <Tag className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Ksh {booking.service.price}</span>
          </div>
        )}
      </div>

      {/* Customer Info */}
      <div className="flex items-center gap-3 pt-2 border-t">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            {booking.customer?.name?.charAt(0)?.toUpperCase() || 
             booking.customer?.email?.charAt(0)?.toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium text-foreground">
            {booking.customer?.name || 'Anonymous Customer'}
          </p>
          <p className="text-sm text-muted-foreground">
            {booking.customer?.email}
          </p>
        </div>
      </div>
    </div>
  );
};